import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, X, Wrench, Lightning, ShieldCheck, Broom, DotsThreeOutline, MagnifyingGlass, Hash, ClockCounterClockwise,
  Car, VideoCamera, Baby, Elevator, Confetti, Drop, BatteryCharging, Microphone, Image as ImageIcon, FileArrowUp, Paperclip
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";

const CATS = [
  { code: "PLUMBING", label: "Plumbing", icon: Wrench },
  { code: "ELECTRICAL", label: "Electrical", icon: Lightning },
  { code: "SECURITY", label: "Security", icon: ShieldCheck },
  { code: "CLEANING", label: "Cleaning", icon: Broom },
  { code: "PARKING", label: "Parking", icon: Car },
  { code: "CCTV", label: "CCTV", icon: VideoCamera },
  { code: "KIDS_AREA", label: "Kids Area", icon: Baby },
  { code: "LIFT", label: "Lift", icon: Elevator },
  { code: "AMENITIES", label: "Amenities", icon: Confetti },
  { code: "WATER", label: "Water", icon: Drop },
  { code: "GENERATOR", label: "Generator", icon: BatteryCharging },
  { code: "OTHER", label: "Other", icon: DotsThreeOutline },
];
const STATUS_TINT = {
  OPEN: "border-neon-pink/30 text-neon-pink bg-neon-pink/10",
  IN_PROGRESS: "border-neon-amber/30 text-neon-amber bg-neon-amber/10",
  RESOLVED: "border-neon-lime/30 text-neon-lime bg-neon-lime/10",
  CLOSED: "border-white/15 text-white/60 bg-white/5",
};

export default function Complaints() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const isResident = user?.role === "OWNER" || user?.role === "TENANT";
  const isStaff = !isResident;

  // staff search state
  const [query, setQuery] = useState(params.get("id") || "");
  const [searched, setSearched] = useState([]);
  const [searching, setSearching] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const asOf = params.get("asOf");
      const { data } = await api.get(`/api/complaints${asOf ? `?asOf=${asOf}` : ""}`);
      setList(data.items);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [params.get("asOf")]);

  // open the raise sheet directly when arriving via "Raise a Ticket"
  useEffect(() => {
    if (params.get("raise")) setOpen(true);
    // eslint-disable-next-line
  }, []);

  // auto-search if landed with ?id=
  useEffect(() => {
    const id = params.get("id");
    if (id && isStaff) { setQuery(id); runSearch(id); }
    // eslint-disable-next-line
  }, [params.get("id"), isStaff]);

  async function runSearch(q = query) {
    if (!q.trim()) { setSearched([]); return; }
    setSearching(true);
    try { const { data } = await api.get(`/api/complaints/search?q=${encodeURIComponent(q.trim())}`); setSearched(data.items); }
    finally { setSearching(false); }
  }

  const statusFilter = (params.get("status") || "").toUpperCase();
  const asOf = params.get("asOf");
  const byStatus = statusFilter ? list.filter((c) => String(c.status).toUpperCase() === statusFilter) : list;
  const visible = isStaff
    ? (query.trim() || searched.length > 0 ? searched : byStatus)
    : (query.trim()
        ? byStatus.filter((c) => `${c.ticketId || ""} ${c.title || ""}`.toLowerCase().includes(query.toLowerCase()))
        : byStatus);

  return (
    <div className="space-y-5 font-sans" data-testid="complaints-screen">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-white/50">
            {isStaff ? (query.trim() ? "Search results" : "All tickets") : "Your tickets"}
          </p>
          <h1 className="font-display text-3xl font-extrabold text-white">{t("tickets.title")}</h1>
        </div>
        {isResident && (
          <button data-testid="raise-complaint-fab" onClick={() => setOpen(true)}
            className="flex items-center gap-1.5 rounded-full bg-neon-cyan px-4 py-2.5 text-sm font-bold text-black shadow-glow-cyan active:scale-95">
            <Plus size={16} weight="bold" /> {t("tickets.raise")}
          </button>
        )}
      </div>

      {(statusFilter || asOf) && (
        <div className="flex items-center gap-2 rounded-xl border border-neon-amber/40 bg-neon-amber/10 px-3 py-2 text-sm text-neon-amber">
          <ClockCounterClockwise size={16} weight="bold" />
          <span className="font-semibold">
            {statusFilter ? `${statusFilter.replace("_", " ")} ` : ""}tickets{asOf ? ` · as of ${asOf.split("-").reverse().join("/")}` : ""}
          </span>
          <div className="flex-1" />
          <button onClick={() => { setQuery(""); setParams({}, { replace: true }); }} className="font-bold text-neon-cyan hover:underline">Clear</button>
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); if (isStaff) { runSearch(); setParams(query ? { id: query } : {}, { replace: true }); } }}
        className="flex items-stretch gap-2 overflow-hidden rounded-2xl border border-white/15 bg-white/[0.03] focus-within:border-neon-cyan" data-testid="ticket-search-form">
        <span className="flex items-center px-3 text-white/55"><MagnifyingGlass size={18} weight="bold" /></span>
        <input className="flex-1 bg-transparent px-2 py-3 text-sm text-white outline-none placeholder:text-white/30"
          placeholder="Search by ticket id, title or flat" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="ticket-search-input" />
        {query && (
          <button type="button" onClick={() => { setQuery(""); setSearched([]); if (isStaff) setParams({}, { replace: true }); }}
            className="px-3 text-white/55 hover:text-white" data-testid="ticket-search-clear"><X size={16} weight="bold" /></button>
        )}
        <button type="submit" className="px-3 text-neon-cyan" data-testid="ticket-search-submit"><MagnifyingGlass size={18} weight="fill" /></button>
      </form>

      {loading ? (
        <SkeletonList />
      ) : visible.length === 0 ? (
        <div className="card flex flex-col items-center gap-2 p-10 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-neon-cyan/15 text-neon-cyan"><Wrench size={26} weight="duotone" /></div>
          <p className="font-display text-lg font-semibold text-white">{isStaff && query.trim() ? "No tickets matched" : "No tickets yet"}</p>
          <p className="text-[13px] text-white/55">
            {isStaff && query.trim()
              ? `Try a partial TKT-id, title or flat code.`
              : (isResident ? "Tap Raise to report your first issue." : "When residents raise issues they'll appear here.")}
          </p>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="ticket-list">
          {visible.map((c, i) => (
            <motion.div key={c.id} data-testid={`complaint-${c.ticketId || c.id}`}
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 8) * 0.04 }}
              onClick={() => navigate(`/complaints/${c.id}`)}
              className="card cursor-pointer p-4 transition-all hover:border-white/20 active:scale-[0.99]">
              <div className="flex items-start gap-3">
                <CatIcon code={c.category} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    {c.ticketId && (
                      <span className="inline-flex items-center gap-1 rounded-md border border-neon-cyan/30 bg-neon-cyan/10 px-1.5 py-0.5 font-mono text-[10px] font-bold text-neon-cyan" data-testid={`tid-${c.ticketId}`}>
                        <Hash size={9} weight="bold" />{c.ticketId.replace("TKT-", "")}
                      </span>
                    )}
                    <p className="truncate font-display text-[15px] font-semibold text-white">{c.title}</p>
                  </div>
                  <p className="mt-0.5 line-clamp-2 text-[12px] text-white/60">{c.description}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className={`chip ${STATUS_TINT[c.status] || ""}`}>{c.status.replace("_", " ")}</span>
                    <span className="chip">{c.category}</span>
                    {isStaff && <span className="chip">Flat {c.flatId || "—"}</span>}
                    <span className="text-[10px] uppercase tracking-wider text-white/40">{new Date(c.createdAt).toLocaleDateString()}</span>
                  </div>
                  {isStaff && c.status !== "RESOLVED" && c.status !== "CLOSED" && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <StaffActions complaint={c} onUpdated={() => { load(); if (query.trim()) runSearch(); }} />
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {open && <RaiseSheet onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }} />}
      </AnimatePresence>
    </div>
  );
}

function CatIcon({ code }) {
  const found = CATS.find((c) => c.code === code) || CATS[CATS.length - 1];
  const Icon = found.icon;
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] text-neon-cyan">
      <Icon size={18} weight="duotone" />
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="space-y-2.5">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="card flex items-center gap-3 p-4">
          <div className="h-10 w-10 rounded-2xl bg-white/5 animate-pulse-soft" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-2/3 rounded bg-white/10 animate-pulse-soft" />
            <div className="h-2 w-1/2 rounded bg-white/5 animate-pulse-soft" />
          </div>
        </div>
      ))}
    </div>
  );
}

function StaffActions({ complaint, onUpdated }) {
  const [busy, setBusy] = useState(false);
  async function set(status) {
    setBusy(true);
    try { await api.patch(`/api/complaints/${complaint.id}/status`, { status }); onUpdated(); }
    finally { setBusy(false); }
  }
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {complaint.status !== "IN_PROGRESS" && (
        <button data-testid={`mark-progress-${complaint.ticketId || complaint.id}`} disabled={busy} onClick={() => set("IN_PROGRESS")}
          className="rounded-full border border-neon-amber/40 bg-neon-amber/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-neon-amber">Start work</button>
      )}
      <button data-testid={`mark-resolved-${complaint.ticketId || complaint.id}`} disabled={busy} onClick={() => set("RESOLVED")}
        className="rounded-full border border-neon-lime/40 bg-neon-lime/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-neon-lime">Resolve</button>
    </div>
  );
}

function RaiseSheet({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [cat, setCat] = useState("PLUMBING");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [newTicketId, setNewTicketId] = useState(null);
  const [attachments, setAttachments] = useState([]); // {kind, file, name, size}
  const [recording, setRecording] = useState(false);
  const fileRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  function onPick(e) {
    const f = e.target.files?.[0]; if (e.target) e.target.value = "";
    if (!f) return;
    const isImg = f.type.startsWith("image/");
    const isVid = f.type.startsWith("video/");
    if (!isImg && !isVid) { setErr("Please choose an image or a video."); return; }
    const cap = isImg ? 4 * 1024 * 1024 : 20 * 1024 * 1024;
    if (f.size > cap) { setErr(isImg ? "Image exceeds 4 MB." : "Video exceeds 20 MB."); return; }
    setErr(null);
    setAttachments((a) => [...a, { kind: isImg ? "IMAGE" : "FILE", file: f, name: f.name, size: f.size }]);
  }

  async function toggleMic() {
    if (recording) { try { recRef.current?.stop(); } catch {} return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : (MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "");
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recRef.current = rec; chunksRef.current = [];
      rec.ondataavailable = (ev) => { if (ev.data?.size) chunksRef.current.push(ev.data); };
      rec.onstop = () => {
        try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        const file = new File([blob], `voice-${Date.now()}.webm`, { type: blob.type });
        if (file.size > 4 * 1024 * 1024) setErr("Voice note exceeds 4 MB.");
        else setAttachments((a) => [...a, { kind: "AUDIO", file, name: "Voice note", size: file.size }]);
        setRecording(false);
      };
      rec.start(); setRecording(true); setErr(null);
    } catch { setErr("Microphone permission denied."); }
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post("/api/complaints", { title: title.trim(), description: desc.trim(), category: cat });
      // Wire any attachments to the new ticket (image ≤4 MB / video ≤20 MB / voice ≤4 MB).
      for (const att of attachments) {
        try {
          const fd = new FormData();
          fd.append("kind", att.kind);
          fd.append("file", att.file, att.name);
          await api.post(`/api/complaints/${data.id}/attachments`, fd);
        } catch { /* keep going; ticket is already created */ }
      }
      setNewTicketId(data.ticketId);
      setTimeout(() => onCreated(), 1400);
    } catch {
      setErr("Couldn't submit. Please try again.");
      setBusy(false);
    }
  }

  if (newTicketId) {
    return (
      <motion.div className="fixed inset-0 z-50 flex items-end justify-center"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
        <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          className="relative w-full max-w-md rounded-t-[2rem] border border-white/10 bg-ink-900 p-7 pb-safe text-center" data-testid="ticket-created">
          <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/15" />
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-neon-lime text-black shadow-glow-lime">
            <Hash size={28} weight="bold" />
          </div>
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-neon-lime">Ticket created</p>
          <p className="mt-1 font-mono text-3xl font-extrabold text-white">{newTicketId}</p>
          <p className="mt-1 text-[13px] text-white/55">Save this ID — the office will reference it in updates.</p>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div className="fixed inset-0 z-50 flex items-end justify-center"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.form onSubmit={submit}
        initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
        transition={{ duration: 0.28, ease: [0.2, 0.7, 0.2, 1] }}
        data-testid="raise-sheet"
        className="relative w-full max-w-md rounded-t-[2rem] border border-white/10 bg-ink-900 p-6 pb-safe shadow-[0_-20px_60px_rgba(0,0,0,0.6)]">
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/15" />
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold text-white">Raise a Ticket</h3>
          <button type="button" onClick={onClose} className="rounded-full bg-white/5 p-1.5 text-white/60 hover:text-white" data-testid="raise-close"><X size={16} weight="bold" /></button>
        </div>
        <div className="mt-4 space-y-3">
          <div>
            <label className="label">Category</label>
            <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
              {CATS.map(({ code, label, icon: Icon }) => {
                const active = cat === code;
                return (
                  <button type="button" key={code} data-testid={`cat-${code.toLowerCase()}`} onClick={() => setCat(code)}
                    className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-all ${
                      active ? "border-neon-cyan/60 bg-neon-cyan/15 text-neon-cyan" : "border-white/10 bg-white/[0.04] text-white/70"
                    }`}>
                    <Icon size={14} weight="bold" /> {label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="label">Title</label>
            <input className="field" data-testid="raise-title" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Kitchen tap is leaking" required minLength={3} />
          </div>
          <div>
            <label className="label">Description</label>
            <textarea rows={4} className="field" data-testid="raise-desc" value={desc} onChange={(e) => setDesc(e.target.value)}
              placeholder="Add a few details so the team knows what to expect." required minLength={3} />
          </div>
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {attachments.map((a, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[11px] text-white/75">
                  {a.kind === "IMAGE" ? <ImageIcon size={12} weight="fill" className="text-neon-cyan" /> : a.kind === "AUDIO" ? <Microphone size={12} weight="fill" className="text-neon-pink" /> : <FileArrowUp size={12} weight="fill" className="text-neon-lime" />}
                  <span className="max-w-[8rem] truncate">{a.name}</span>
                  <button type="button" onClick={() => setAttachments((arr) => arr.filter((_, j) => j !== i))} className="text-white/45 hover:text-white"><X size={11} weight="bold" /></button>
                </span>
              ))}
            </div>
          )}
          {err && <p className="rounded-xl border border-neon-pink/30 bg-neon-pink/10 px-3 py-2 text-sm text-neon-pink">{err}</p>}
          {/* + (image ≤4 MB / video ≤20 MB) · Mic (voice note) · Submit — all in one row */}
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={onPick} />
            <button type="button" data-testid="raise-attach" onClick={() => fileRef.current?.click()}
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5 text-neon-cyan hover:bg-white/10">
              <Plus size={20} weight="bold" />
            </button>
            <button type="button" data-testid="raise-mic" onClick={toggleMic}
              className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full border transition ${recording ? "border-neon-pink/60 bg-neon-pink/15 text-neon-pink animate-pulse-soft" : "border-white/10 bg-white/5 text-neon-pink hover:bg-white/10"}`}>
              <Microphone size={20} weight="fill" />
            </button>
            <button data-testid="raise-submit" className="btn-primary flex-1" disabled={busy || recording}>
              {busy ? "Submitting…" : "Submit Ticket"}
            </button>
          </div>
        </div>
      </motion.form>
    </motion.div>
  );
}
